import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaBalanceScale,
  FaChartLine,
  FaCalculator,
  FaFileInvoiceDollar,
  FaLightbulb,
  FaShieldAlt,
  FaHandshake,
  FaUserShield,
  FaCheck
} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 12px;

  @media (max-width: 1024px) {
    width: 90%;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
    width: 100%;
  }
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #E60000;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const LeftSection = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  max-height: 400px;
  animation: ${slideInLeft} 1s ease-in-out;
  border-radius: 8px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease-in-out;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ExperienceBadge = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  padding: 10px 18px;
  font-size: 18px;
  font-weight: bold;
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const FeatureCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  background: ${props => props.bgColor || 'linear-gradient(to right, #228B22, #E60000)'};
  color: white;
  font-size: 24px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
`;

const FeatureTitle = styled.h4`
  font-weight: bold;
  margin-bottom: 12px;
  color: #333;
  font-size: 20px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  font-size: 16px;
  color: #555;
  
  svg {
    color: #228B22;
    margin-right: 10px;
    margin-top: 4px;
    flex-shrink: 0;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
  }
  
  svg {
    font-size: 40px;
    margin-bottom: 15px;
  }
  
  p {
    font-weight: bold;
    margin-top: 10px;
    font-size: 18px;
  }
`;

const SectionTitle = styled.h3`
  position: relative;
  font-weight: bold;
  margin-bottom: 40px;
  padding-bottom: 15px;
  text-align: center;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #228B22, #E60000);
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  margin: 40px auto 0 auto; /* Centre horizontalement */
  text-align: center;
  width: 50%;
  display: block; /* Nécessaire pour margin auto */

  h4 {
    font-weight: bold;
    margin-bottom: 20px;
  }

  button {
    background: white;
    color: #333;
    border: none;
    padding: 10px 25px;
    border-radius: 6px;
    font-weight: bold;
    transition: all 0.3s ease;
    margin-top: 15px;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
  }
`;


const AccountingAudit = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const faqSection = document.getElementById("faq-section");
      if (faqSection) {
        const rect = faqSection.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
    {
      question: "Pourquoi faire appel à un expert-comptable pour ma comptabilité ?",
      answer: "Un expert-comptable vous assure une comptabilité rigoureuse et conforme aux normes, vous libère des tâches chronophages, vous conseille en matière financière et fiscale, et vous aide à prendre des décisions éclairées pour votre entreprise."
    },
    {
      question: "Quelles sont les obligations comptables pour mon entreprise ?",
      answer: "Les obligations varient selon la taille et la forme juridique de votre entreprise. Elles incluent généralement la tenue de livres comptables, l'établissement des comptes annuels (bilan, compte de résultat, annexe) et les déclarations fiscales."
    },
    {
      question: "Comment choisir le bon expert-comptable pour mon entreprise ?",
      answer: "Il est important de choisir un expert-comptable avec une expertise adaptée à votre secteur d'activité, une bonne communication, et qui propose des services personnalisés."
    },
    {
      question: "Quelle est la différence entre un expert-comptable et un comptable ?",
      answer: "L'expert-comptable est un professionnel diplômé et inscrit à l'Ordre des experts-comptables, garantissant son expertise et son éthique. Il peut réaliser des missions d'audit et de conseil, en plus de la tenue comptable. Le comptable a un champ de compétence plus restreint."
    },
    {
      question: "Quels services de comptabilité propose CINEF-EC ?",
      answer: "Nous proposons une gamme complète de services : tenue de comptabilité, établissement des comptes annuels, déclarations fiscales (TVA, impôt sur les sociétés, etc.), gestion de la paie, conseil en gestion financière, et bien plus encore."
    },
    {
      question: "Pouvez-vous m'aider à optimiser ma fiscalité ?",
      answer: "Absolument. Nous analysons votre situation fiscale et vous conseillons sur les stratégies d'optimisation légales pour réduire votre charge fiscale."
    },
    {
      question: "Comment sont fixés vos tarifs ?",
      answer: "Nos tarifs dépendent de la complexité de votre comptabilité et des services demandés. Nous établissons un devis personnalisé après avoir analysé vos besoins."
    }
  ];

  return (
    <>
      <Header />

      <section className="service-banner d-flex align-items-center">
        <div className="container text-white text-start">
          <h1 className="fw-bold">Comptabilité & Audit</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/" className="text-white text-decoration-none">
                  Accueil
                </Link>{" "}
                /{" "}
                <Link to="/services" className="text-white text-decoration-none">
                  Services
                </Link>
              </li>
              <li className="breadcrumb-item active text-white" aria-current="page">
                Comptabilité & Audit
              </li>
            </ol>
          </nav>
        </div>
      </section>

      <section className="service-content py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="fw-bold">Expertise comptable pour votre entreprise</h2>
              <div className="separator mx-auto my-3"></div>
              <p className="lead text-muted">
                La gestion comptable et fiscale est essentielle pour la pérennité et le développement de votre entreprise.
                CINEF-EC vous propose un accompagnement personnalisé pour vous libérer de ces contraintes et vous permettre
                de vous concentrer sur votre cœur de métier.
              </p>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-md-6">
              <FeatureCard>
                <FeatureIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                  <FaUserShield />
                </FeatureIcon>
                <FeatureTitle>Expertise reconnue</FeatureTitle>
                <p>
                  Notre équipe d'experts-comptables qualifiés maîtrise les subtilités de la réglementation
                  comptable et fiscale pour vous offrir un service de la plus haute qualité.
                </p>
              </FeatureCard>
            </div>
            <div className="col-md-6">
              <FeatureCard>
                <FeatureIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                  <FaHandshake />
                </FeatureIcon>
                <FeatureTitle>Accompagnement sur mesure</FeatureTitle>
                <p>
                  Nous adaptons nos services à vos besoins spécifiques, que vous soyez une TPE,
                  une PME ou un grand groupe, pour un accompagnement véritablement personnalisé.
                </p>
              </FeatureCard>
            </div>
            <div className="col-md-6">
              <FeatureCard>
                <FeatureIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                  <FaShieldAlt />
                </FeatureIcon>
                <FeatureTitle>Conformité et sécurité</FeatureTitle>
                <p>
                  Nous assurons la tenue de votre comptabilité et le respect de vos obligations fiscales
                  en toute conformité et sécurité, pour vous éviter tout risque juridique ou fiscal.
                </p>
              </FeatureCard>
            </div>
            <div className="col-md-6">
              <FeatureCard>
                <FeatureIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                  <FaLightbulb />
                </FeatureIcon>
                <FeatureTitle>Conseils proactifs</FeatureTitle>
                <p>
                  Nous vous conseillons sur les meilleures stratégies pour optimiser votre fiscalité
                  et améliorer votre performance financière dans une approche proactive.
                </p>
              </FeatureCard>
            </div>
            <div className="col-md-6 mx-auto">
              <FeatureCard>
                <FeatureIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                  <FaChartLine />
                </FeatureIcon>
                <FeatureTitle>Outils modernes</FeatureTitle>
                <p>
                  Nous mettons à votre disposition des outils digitaux performants pour faciliter la gestion
                  de votre comptabilité et la communication avec notre cabinet.
                </p>
              </FeatureCard>
            </div>
          </div>
        </div>
      </section>

      <section className="services-section py-5 bg-light">
        <div className="container">
          <SectionTitle>Nos Services Comptables</SectionTitle>
          <div className="row g-4">
            <div className="col-md-4">
              <ServiceCard>
                <FaCalculator className="text-success" />
                <FeatureTitle>Tenue de comptabilité</FeatureTitle>
                <p className="text-muted">
                  Saisie des écritures, établissement des comptes annuels, suivi de trésorerie.
                </p>
              </ServiceCard>
            </div>
            <div className="col-md-4">
              <ServiceCard>
                <FaFileInvoiceDollar className="text-danger" />
                <FeatureTitle>Déclarations fiscales</FeatureTitle>
                <p className="text-muted">
                  TVA, impôt sur les sociétés, impôt sur le revenu, déclarations sociales.
                </p>
              </ServiceCard>
            </div>
            <div className="col-md-4">
              <ServiceCard>
                <FaBalanceScale className="text-primary" />
                <FeatureTitle>Conseil fiscal</FeatureTitle>
                <p className="text-muted">
                  Optimisation fiscale, accompagnement en cas de contrôle fiscal, veille juridique et fiscale.
                </p>
              </ServiceCard>
            </div>
            <div className="col-md-6">
              <ServiceCard>
                <FaChartLine className="text-warning" />
                <FeatureTitle>Prévisions et tableaux de bord</FeatureTitle>
                <p className="text-muted">
                  Établissement de budgets prévisionnels et de tableaux de bord pour piloter votre activité.
                </p>
              </ServiceCard>
            </div>
            <div className="col-md-6">
              <ServiceCard>
                <FaLightbulb className="text-info" />
                <FeatureTitle>Analyse financière</FeatureTitle>
                <p className="text-muted">
                  Analyse financière et gestion de la performance pour optimiser vos résultats.
                </p>
              </ServiceCard>
            </div>
          </div>

          <ContactCard>
            <h4>Votre partenaire de confiance</h4>
            <p>
              Nous sommes votre partenaire de confiance pour une gestion comptable et fiscale efficace et sereine.
              Contactez-nous dès aujourd'hui pour une première consultation gratuite et découvrez comment nous
              pouvons vous aider à atteindre vos objectifs.
            </p>
            <Link to="/contact" className="btn">
              Nous contacter
            </Link>
          </ContactCard>
        </div>
      </section>

      <section className="advantages py-5">
        <div className="container">
          <SectionTitle>Avantages de nos services</SectionTitle>
          <div className="row g-4">
            <div className="col-md-3">
              <ServiceCard>
                <FaUserShield className="text-primary" />
                <p>Conseil Expert</p>
              </ServiceCard>
            </div>
            <div className="col-md-3">
              <ServiceCard>
                <FaShieldAlt className="text-success" />
                <p>Réduction des Risques</p>
              </ServiceCard>
            </div>
            <div className="col-md-3">
              <ServiceCard>
                <FaHandshake className="text-danger" />
                <p>Résolution de Conflits</p>
              </ServiceCard>
            </div>
            <div className="col-md-3">
              <ServiceCard>
                <FaLightbulb className="text-warning" />
                <p>Tranquillité d'Esprit</p>
              </ServiceCard>
            </div>
          </div>
        </div>
      </section>

      <section id="faq-section" className={`container-fluid py-5 bg-light ${isVisible ? 'visible' : ''}`}>
        <div className="container">
          <SectionTitle>Questions Fréquentes</SectionTitle>
          <FAQContainer>


            <FAQContent>
              {faqData.map((faq, index) => (
                <FAQItem key={index} delay={`${index * 0.2}s`}>
                  <Question onClick={() => toggleFAQ(index)}>
                    {faq.question}
                    <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                  </Question>
                  {activeIndex === index && <Answer>{faq.answer}</Answer>}
                </FAQItem>
              ))}
            </FAQContent>
          </FAQContainer>
        </div>
      </section>

      <Footer />

      <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.8), rgba(220, 53, 69, 0.8)), 
                      url("/assets/images/image7.png");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }
        
        .separator {
          height: 4px;
          width: 80px;
          background: linear-gradient(to right, #228B22, #E60000);
          border-radius: 2px;
        }
        
        .bg-light {
          background-color: #f8f9fa;
        }
        
        .lead {
          font-size: 1.2rem;
          line-height: 1.6;
        }
        
        #faq-section {
          transition: opacity 0.5s ease, transform 0.5s ease;
          opacity: 0;
          transform: translateY(20px);
        }
        
        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </>
  );
};

export default AccountingAudit;